import { useQuery } from "@apollo/client";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { isEmpty } from "lodash";
import React, { createContext, useMemo } from "react";
import { useSetAIError } from "../../chat/ai-chat/hooks/AIErrorContext";
import { useUserProfile } from "../../hooks/useUserProfile";
import { TopCollaboratorsDocument } from "../apollo/__generated__/TopCollaborators.generated";
import { getRangeForLastMonth } from "./getRangeForLastMonth";

export type CollaboratorStats = {
  calendarId: string | null;
  topCollaborators: {
    calendarId: string | null;
    sharedMinutes: number | null;
    orgPerson: {
      id: string;
      primaryCalendarId: string | null;
      profile: {
        givenName: string | null;
        familyName: string | null;
        externalImageUrl: string | null;
      } | null;
    };
  }[];
};

type TopCollaborators = CollaboratorStats["topCollaborators"];
export type TopCollaborator = CollaboratorStats["topCollaborators"][0];

type TopCollaboratorsContextValue = {
  topCollaborators: TopCollaborators;
  loading: boolean;
};

const emptyTopCollaboratorsValue = {
  topCollaborators: [],
  loading: false,
};

const TopCollaboratorsContext = createContext<TopCollaboratorsContextValue>(
  emptyTopCollaboratorsValue,
);

export const useTopCollaborators = () => {
  const context = React.useContext(TopCollaboratorsContext);
  if (!context) {
    throw new Error("useTopCollaborators must be used within a TopCollaboratorsContext");
  }
  return context;
};

export const TopCollaboratorsProvider = ({ children }: { children: React.ReactNode }) => {
  const setError = useSetAIError();
  const timeZone = getRenderTimeZone();
  const { rangeMax, rangeMin } = getRangeForLastMonth(timeZone);

  const {
    userProfile: { primaryEmail },
  } = useUserProfile();

  const { data, loading } = useQuery(TopCollaboratorsDocument, {
    variables: {
      timeZone,
      calendarId: primaryEmail!,
      rangeMax,
      rangeMin,
    },
    skip: !primaryEmail,
    onError: (error: Error) => {
      setError({
        error: error,
        showUserMessage: false,
        message: "Failed to fetch top collaborators",
      });
    },
  });

  const topCollaborators = useMemo(() => {
    const collaboratorStats = data?.viewer.user?.orgs.edges?.[0]?.node?.collaboratorStatsErrorable;
    if (collaboratorStats && collaboratorStats.__typename === "CollaboratorStats") {
      if (!!collaboratorStats.topCollaborators && !isEmpty(collaboratorStats.topCollaborators)) {
        return collaboratorStats.topCollaborators;
      }
    }
    return [];
  }, [data?.viewer.user?.orgs.edges]);

  const value = useMemo(() => ({ topCollaborators, loading }), [topCollaborators, loading]);

  return (
    <TopCollaboratorsContext.Provider value={value}>{children}</TopCollaboratorsContext.Provider>
  );
};
